import { RealtimeChannel } from '@supabase/supabase-js';
import posthog from 'posthog-js';
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ANALYTICS_EVENTS } from 'src/analytics/events';
import { clearClientCaches, prefetchTopClients } from 'src/services/clientPrefetchCache';
import { Client, clientService, CreateClientData } from 'src/services/clientService';
import { getCurrentUser } from 'src/utils/supabase';

import { useProfile } from './ProfileContext';

const CLIENTS_CACHE_KEY = 'tropel_clients_cache_v1';

interface CachedClients {
  profileId: string;
  clients: Client[];
  cachedAt: number;
}

const loadCachedClients = (): CachedClients | null => {
  try {
    const raw = localStorage.getItem(CLIENTS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedClients;
    if (parsed && typeof parsed.profileId === 'string' && Array.isArray(parsed.clients)) {
      return parsed;
    }
  } catch {}
  return null;
};

const saveCachedClients = (profileId: string, clients: Client[]) => {
  try {
    localStorage.setItem(CLIENTS_CACHE_KEY, JSON.stringify({ profileId, clients, cachedAt: Date.now() }));
  } catch {}
};

const clearCachedClients = () => {
  try {
    localStorage.removeItem(CLIENTS_CACHE_KEY);
  } catch {}
};

interface ClientContextType {
  clients: Client[];
  selectedClient: Client | null;
  loading: boolean;
  error: string | null;
  fetchClients: () => Promise<void>;
  createClient: (clientData: CreateClientData) => Promise<Client>;
  updateClient: (clientId: string, updates: Partial<CreateClientData>) => Promise<Client>;
  deleteClient: (clientId: string) => Promise<void>;
  setSelectedClient: (client: Client | null) => void;
  clearError: () => void;
}

const ClientContext = createContext<ClientContextType | undefined>(undefined);

export const useClients = () => {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error('useClients must be used within a ClientProvider');
  }
  return context;
};

interface ClientProviderProps {
  children: ReactNode;
}

export const ClientProvider: React.FC<ClientProviderProps> = ({ children }) => {
  const initialCache = loadCachedClients();
  const [clients, setClients] = useState<Client[]>(initialCache?.clients ?? []);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(!initialCache);
  const [error, setError] = useState<string | null>(null);

  const { profile, loading: profileLoading } = useProfile();

  const fetchClients = useCallback(async () => {
    if (!profile?.id) {
      setLoading(false);
      return;
    }

    const cached = loadCachedClients();
    const hasFreshCache = cached?.profileId === profile.id;

    if (!hasFreshCache) {
      setLoading(true);
    }
    setError(null);

    try {
      const fetchedClients = await clientService.getClients(profile.id);
      setClients(fetchedClients);
      saveCachedClients(profile.id, fetchedClients);
      const targets = fetchedClients.map((c) => ({ id: c.id, name: c.name }));
      prefetchTopClients(targets, 10, profile.id).catch(() => {});
    } catch {
      posthog?.capture(ANALYTICS_EVENTS.API_ERROR, { action: 'fetch_clients' });
      if (!hasFreshCache) {
        setError('Failed to fetch clients');
      }
    } finally {
      setLoading(false);
    }
  }, [profile?.id]);

  const createClient = useCallback(
    async (clientData: CreateClientData): Promise<Client> => {
      setError(null);

      try {
        const newClient = await clientService.createClient(clientData);
        setClients((prev) => {
          const next = prev.some((c) => c.id === newClient.id) ? prev : [newClient, ...prev];
          if (profile?.id) saveCachedClients(profile.id, next);
          return next;
        });
        return newClient;
      } catch (err) {
        posthog?.capture(ANALYTICS_EVENTS.API_ERROR, { action: 'create_client' });
        setError('Failed to create client');
        throw err;
      }
    },
    [profile?.id]
  );

  const updateClient = useCallback(
    async (clientId: string, updates: Partial<CreateClientData>): Promise<Client> => {
      setError(null);

      try {
        const updatedClient = await clientService.updateClient(clientId, updates);
        setClients((prev) => {
          const next = prev.map((client) => (client.id === clientId ? updatedClient : client));
          if (profile?.id) saveCachedClients(profile.id, next);
          return next;
        });

        if (selectedClient?.id === clientId) {
          setSelectedClient(updatedClient);
        }

        return updatedClient;
      } catch (err) {
        setError('Failed to update client');
        throw err;
      }
    },
    [profile?.id, selectedClient?.id]
  );

  const deleteClient = useCallback(
    async (clientId: string) => {
      setError(null);

      try {
        await clientService.deleteClient(clientId);
        setClients((prev) => {
          const next = prev.filter((client) => client.id !== clientId);
          if (profile?.id) saveCachedClients(profile.id, next);
          return next;
        });

        if (selectedClient?.id === clientId) {
          setSelectedClient(null);
        }
      } catch (err) {
        setError('Failed to delete client');
        throw err;
      }
    },
    [profile?.id, selectedClient?.id]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Fetch clients when profile is available. We keep the locally-cached client
  // list visible while the network request is in flight so the sidebar never
  // flashes empty on reload (stale-while-revalidate).
  useEffect(() => {
    if (profileLoading) {
      // If we already have a cached list, don't flip loading to true and erase
      // it. The fetch below will refresh it once profile resolves.
      return;
    }

    if (profile?.id) {
      const cached = loadCachedClients();
      if (cached && cached.profileId !== profile.id) {
        clearCachedClients();
        clearClientCaches();
        setClients([]);
      }
      fetchClients();
    } else {
      clearCachedClients();
      clearClientCaches();
      setClients([]);
      setLoading(false);
    }
  }, [profile?.id, profileLoading, fetchClients]);

  // Set up real-time subscriptions for clients
  useEffect(() => {
    if (!profile?.id) return;

    let isMounted = true;
    let channel: RealtimeChannel | null | undefined;

    const setupSubscription = async () => {
      try {
        const user = await getCurrentUser();
        if (!user || !isMounted) return;

        // Set up subscription for client updates
        channel = await clientService.subscribeToClientUpdates(
          user.id,
          () => {
            if (isMounted) {
              fetchClients();
            }
          },
          () => {}
        );
      } catch (err) {}
    };

    setupSubscription();

    return () => {
      isMounted = false;
      if (channel) {
        channel.unsubscribe();
      }
    };
  }, [profile?.id, fetchClients]);

  // Combine loading states: show loading if profile is loading OR clients are loading
  const isLoading = profileLoading || loading;

  const value: ClientContextType = useMemo(
    () => ({
      clients,
      selectedClient,
      loading: isLoading,
      error,
      fetchClients,
      createClient,
      updateClient,
      deleteClient,
      setSelectedClient,
      clearError,
    }),
    [clients, selectedClient, isLoading, error, fetchClients, createClient, updateClient, deleteClient, clearError]
  );

  return <ClientContext.Provider value={value}>{children}</ClientContext.Provider>;
};
